import Box from "@mui/material/Box";

import { StyledAnchor } from "@/components/common/styles";
import { marketingHref } from "@/components/marketing/marketingLinks";
import { EdgeMask, InlineSvg, Marquee } from "@/components/marketing/Marquee";
import {
  ACCENT_DEEP,
  MID,
  NOT_WIDE,
  marketingPalette,
} from "@/components/marketing/tokens";

/** A corner control: its words and where it goes, always in a new tab. */
type DeveloperCta = { label: string; href: string };

/** One mark in an integrations row; `wide` is `.marquee-logos_image.is-bigger`. */
type DeveloperLogo = { src: string; wide?: boolean };

/** `.dev-btn_wrap`, the white pill anchored in the corner of both panels. */
function CornerPill({ cta }: { cta: DeveloperCta }) {
  return (
    <StyledAnchor
      href={marketingHref(cta.href)}
      target="_blank"
      rel="noopener"
      sx={{
        position: "absolute",
        inset: "auto 16px 16px auto",
        zIndex: 3,
        display: "flex",
        alignItems: "center",
        gap: "8px",
        p: "6.08px 12px",
        borderRadius: "9999px",
        backgroundColor: "background.default",
        color: marketingPalette.ink,
        fontSize: "0.88rem",
        fontWeight: 500,
        boxShadow: "0 2px 7px rgba(9, 25, 40, 0.13)",
        transition: "all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1)",
        "&:hover": { color: ACCENT_DEEP },
      }}
    >
      {cta.label}
      <InlineSvg viewBox="0 0 12 12" size={16}>
        <path
          d="M2 6H10"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M6.5 2.5L10 6L6.5 9.5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </InlineSvg>
    </StyledAnchor>
  );
}

/**
 * `.why-web-item_grid.is-dev`: the API card and the integrations panel under a
 * developer heading, the home page's and the landing pages' alike. The API
 * card crops its artwork under two fades; the integrations panel runs its rows
 * of marks in opposite directions between two edge masks.
 */
export default function DeveloperPanels({
  apiCard,
  apiCta,
  integrationsCta,
  rows,
}: {
  apiCard: string;
  apiCta: DeveloperCta;
  integrationsCta: DeveloperCta;
  rows: ReadonlyArray<ReadonlyArray<DeveloperLogo>>;
}) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(12, 1fr)",
        gap: "16px",
        width: 1,
        // The two panels are so unlike in shape that the published stylesheet
        // abandons the grid a step earlier here than for the card row above.
        [NOT_WIDE]: { display: "flex", flexDirection: "column" },
      }}
    >
      <Box
        sx={{
          gridColumn: "span 4",
          position: "relative",
          maxHeight: "23.5rem",
          p: "16px",
          borderRadius: "16px",
          backgroundColor: marketingPalette.darkGround,
          overflow: "hidden",
          [NOT_WIDE]: { maxHeight: "30rem" },
          // Once the panel is full width the card stops growing with the
          // artwork and takes a fixed height, cropping it.
          [MID]: { height: "25rem" },
        }}
      >
        <Box
          component="img"
          src={apiCard}
          alt=""
          loading="lazy"
          sx={{ display: "block", width: 1, height: "auto" }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: "0 0 auto",
            zIndex: 2,
            height: "5rem",
            background: `linear-gradient(${marketingPalette.darkGround}, rgba(7, 23, 39, 0))`,
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: "auto 0 0",
            zIndex: 2,
            height: "5rem",
            background: `linear-gradient(rgba(7, 23, 39, 0), ${marketingPalette.darkGround})`,
          }}
        />
        <CornerPill cta={apiCta} />
      </Box>
      <Box
        sx={{
          gridColumn: "span 8",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          width: 1,
          maxWidth: "46.88rem",
          p: "16px 16px 64px",
          borderRadius: "16px",
          backgroundColor: marketingPalette.surface,
          overflow: "hidden",
        }}
      >
        <EdgeMask
          angle="90deg"
          ground={marketingPalette.featureCard}
          sx={{ inset: "0 auto 0 0", width: "6vw", zIndex: 1 }}
        />
        <EdgeMask
          angle="-90deg"
          ground={marketingPalette.featureCard}
          sx={{ inset: "0 0 0 auto", width: "6vw", zIndex: 1 }}
        />
        {rows.map((logos, track) => (
          <Marquee
            key={logos[0].src}
            duration={40}
            reverse={track === 1}
            gap="0.5rem"
          >
            {logos.map((logo) => (
              <Box
                key={logo.src}
                sx={{
                  position: "relative",
                  flex: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "9rem",
                  height: "9rem",
                  borderRadius: "1rem",
                  backgroundColor: "background.default",
                  border: "1px solid #E3E4F3",
                  [MID]: { width: "6rem", height: "6rem" },
                }}
              >
                <Box
                  component="img"
                  src={logo.src}
                  alt=""
                  loading="lazy"
                  sx={{
                    position: "absolute",
                    objectFit: "contain",
                    width: "wide" in logo && logo.wide ? "70%" : "40%",
                    height: "wide" in logo && logo.wide ? "50%" : "40%",
                  }}
                />
              </Box>
            ))}
          </Marquee>
        ))}
        <CornerPill cta={integrationsCta} />
      </Box>
    </Box>
  );
}
